/**
 * ч.1303 — «зберегти в Excel» для друкованої форми (рішення власника 2026-10-16).
 *
 * Друкована форма — звичайна розмітка (`.print-page`): заголовки, абзаци, рядки
 * «підпис: значення», таблиці з обʼєднаннями. Тут вона розбирається на опис аркуша
 * (рядки → комірки з жирністю, вирівнюванням, colspan/rowspan, числами), а книгу
 * складає сервер (`POST /print/xlsx`, openpyxl) — той самий пакет, що й Excel звітів.
 * Службове (кнопки `.print-hide`, картинки печаток, стилі) у файл не потрапляє.
 */
export interface XlsxCell {
  v: string;
  bold?: boolean;
  italic?: boolean;
  align?: 'left' | 'center' | 'right';
  colspan?: number;
  rowspan?: number;
  num?: boolean;
  border?: boolean;
  wrap?: boolean;
  size?: 'h1' | 'h2' | 'h3';
}
export interface XlsxRow { cells: XlsxCell[]; full_width?: boolean }
export interface PrintXlsxPayload {
  filename: string;
  sheet_name?: string;
  landscape?: boolean;
  rows: XlsxRow[];
  col_widths: number[];
}

const BLOCK_SELECTOR = 'div,p,h1,h2,h3,h4,h5,h6,ul,ol,li,table,section,article,header,footer,address,pre,blockquote,dl,dt,dd,fieldset,form,nav,aside,main,figure,hr';
const SKIP_TAGS = new Set(['STYLE', 'SCRIPT', 'BUTTON', 'IMG', 'SVG', 'CANVAS', 'INPUT', 'SELECT', 'TEXTAREA', 'NOSCRIPT', 'VIDEO', 'AUDIO', 'IFRAME']);
const HEADING_SIZE: Record<string, 'h1' | 'h2' | 'h3'> = { H1: 'h1', H2: 'h2', H3: 'h3' };

function style(el: Element): CSSStyleDeclaration | null {
  try { return typeof window !== 'undefined' && window.getComputedStyle ? window.getComputedStyle(el) : null; }
  catch { return null; }
}

/** Службове або сховане: у файл не йде. */
export function isSkipped(el: Element): boolean {
  if (SKIP_TAGS.has(el.tagName)) return true;
  if (el.classList.contains('print-hide') || el.classList.contains('actions')) return true;
  if ((el as HTMLElement).hidden) return true;
  const inline = (el as HTMLElement).style;
  if (inline && (inline.display === 'none' || inline.visibility === 'hidden')) return true;
  const cs = style(el);
  return !!cs && (cs.display === 'none' || cs.visibility === 'hidden');
}

/** Текст елемента з пробілами між сусідніми елементами і переносами за <br>. */
export function inlineText(el: Element): string {
  let out = '';
  el.childNodes.forEach((n) => {
    if (n.nodeType === Node.TEXT_NODE) { out += n.textContent ?? ''; return; }
    if (n.nodeType !== Node.ELEMENT_NODE) return;
    const e = n as Element;
    if (e.tagName === 'BR') { out += '\n'; return; }
    if (isSkipped(e)) return;
    out += ' ' + inlineText(e) + ' ';
  });
  return out;
}

export function cleanText(raw: string): string {
  return raw
    .replace(/[  ]/g, ' ')
    .split('\n')
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter((l) => l.length > 0)
    .join('\n');
}

/**
 * Число у комірці таблиці → «1234.56» (для Excel числом), інакше null.
 * Коди з провідним нулем, дати (дві крапки), довгі цілі — текст.
 */
export function parseCellNumber(text: string): string | null {
  const t = text.replace(/\s/g, '');
  if (!/^-?\d+([.,]\d+)?$/.test(t)) return null;
  if (/^-?0\d/.test(t)) return null;
  if (!/[.,]/.test(t) && t.replace('-', '').length > 12) return null;
  return t.replace(',', '.');
}

function isBold(el: Element): boolean {
  if (/^(H[1-6]|TH|STRONG|B)$/.test(el.tagName)) return true;
  const cs = style(el);
  const w = cs?.fontWeight ?? '';
  if (w === 'bold' || w === 'bolder' || (Number(w) >= 600)) return true;
  // єдина дитина — <strong>/<b> з усім текстом
  const kids = Array.from(el.children).filter((c) => !isSkipped(c));
  return kids.length === 1 && /^(STRONG|B)$/.test(kids[0]!.tagName)
    && cleanText(inlineText(kids[0]!)) === cleanText(inlineText(el));
}

function alignOf(el: Element): 'left' | 'center' | 'right' | undefined {
  const inline = (el as HTMLElement).style?.textAlign;
  const cs = style(el);
  const a = inline || cs?.textAlign || '';
  if (a === 'center' || a === 'right') return a;
  if (el.classList.contains('center')) return 'center';
  if (el.classList.contains('right') || el.classList.contains('num')) return 'right';
  return undefined;
}

function hasBorder(cell: Element): boolean {
  const cs = style(cell);
  if (!cs) return false;
  const w = parseFloat(cs.borderBottomWidth || '0') || parseFloat(cs.borderTopWidth || '0');
  const st = cs.borderBottomStyle || cs.borderTopStyle;
  return w > 0 && !!st && st !== 'none' && st !== 'hidden';
}

class SheetBuilder {
  rows: XlsxRow[] = [];
  colMax: number[] = [];
  tableCols = 0;
  private lastBlank = true;

  blank(): void {
    if (this.lastBlank) return;
    this.rows.push({ cells: [] });
    this.lastBlank = true;
  }

  text(el: Element): void {
    const text = cleanText(inlineText(el));
    if (!text) return;
    this.rows.push({
      cells: [{
        v: text, bold: isBold(el) || undefined, italic: /^(EM|I)$/.test(el.tagName) || undefined,
        align: alignOf(el), size: HEADING_SIZE[el.tagName], wrap: true,
      }],
      full_width: true,
    });
    this.lastBlank = false;
  }

  table(table: HTMLTableElement): void {
    this.blank();
    const occupied = new Set<string>();   // «r:c» зайняті rowspan згори
    const rows = Array.from(table.rows).filter((tr) => !isSkipped(tr));
    rows.forEach((tr, ri) => {
      const cells: XlsxCell[] = [];
      let col = 0;
      Array.from(tr.cells).forEach((td) => {
        if (isSkipped(td)) return;
        while (occupied.has(`${ri}:${col}`)) col += 1;
        const text = cleanText(inlineText(td));
        const colspan = Math.max(1, td.colSpan || 1);
        const rowspan = Math.max(1, td.rowSpan || 1);
        const num = td.tagName === 'TD' ? parseCellNumber(text) : null;
        cells.push({
          v: num ?? text, colspan, rowspan,
          bold: isBold(td) || undefined,
          align: alignOf(td) ?? (num ? 'right' : undefined),
          num: num ? true : undefined,
          border: hasBorder(td) || undefined,
          wrap: text.length > 40 || text.includes('\n') || undefined,
        });
        if (colspan === 1) {
          const longest = text.split('\n').reduce((m, l) => Math.max(m, l.length), 0);
          this.colMax[col] = Math.max(this.colMax[col] ?? 0, longest);
        }
        for (let rr = 0; rr < rowspan; rr += 1) {
          for (let cc = 0; cc < colspan; cc += 1) {
            if (rr > 0 || cc > 0) occupied.add(`${ri + rr}:${col + cc}`);
          }
        }
        col += colspan;
      });
      this.tableCols = Math.max(this.tableCols, col);
      if (cells.length) { this.rows.push({ cells }); this.lastBlank = false; }
    });
    this.blank();
  }

  walk(el: Element): void {
    if (isSkipped(el)) return;
    if (el.tagName === 'TABLE') { this.table(el as HTMLTableElement); return; }
    if (el.tagName === 'HR') { this.blank(); return; }
    if (!el.querySelector(BLOCK_SELECTOR)) { this.text(el); return; }
    // змішаний вміст: текст між блоками — окремими рядками
    let run: Node[] = [];
    const flush = () => {
      if (!run.length) return;
      const span = document.createElement('span');
      run.forEach((n) => span.appendChild(n.cloneNode(true)));
      this.text(span);
      run = [];
    };
    el.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) { if ((n.textContent ?? '').trim()) run.push(n); return; }
      if (n.nodeType !== Node.ELEMENT_NODE) return;
      const e = n as Element;
      if (isSkipped(e)) return;
      if (e.tagName === 'TABLE' || e.tagName === 'HR' || e.matches(BLOCK_SELECTOR)) { flush(); this.walk(e); }
      else run.push(e);
    });
    flush();
  }
}

/** Імʼя файла за замовчуванням — перший заголовок форми («Податкова декларація…»), не назва вкладки. */
export function defaultPrintFilename(root: HTMLElement): string {
  const h = Array.from(root.querySelectorAll('h1,h2,h3')).find((e) => !isSkipped(e) && cleanText(inlineText(e)));
  const t = h ? cleanText(inlineText(h)).split('\n')[0]! : '';
  return (t || 'Документ').slice(0, 80);
}

/** Розібрати друковану форму на опис аркуша для `POST /print/xlsx`. */
export function extractPrintSheet(root: HTMLElement, filename: string): PrintXlsxPayload {
  const b = new SheetBuilder();
  b.walk(root);
  while (b.rows.length && b.rows[b.rows.length - 1]!.cells.length === 0) b.rows.pop();
  while (b.rows.length && b.rows[0]!.cells.length === 0) b.rows.shift();
  const ncols = Math.max(1, b.tableCols);
  const col_widths = ncols === 1 && b.tableCols === 0
    ? [110]
    : Array.from({ length: ncols }, (_, i) => Math.max(8, Math.min(60, (b.colMax[i] ?? 10) + 2)));
  return {
    filename,
    sheet_name: 'Документ',
    landscape: (root.scrollWidth || 0) > 950,
    rows: b.rows,
    col_widths,
  };
}
